import { useEffect, useState } from 'react'

import { create } from 'zustand'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { CheckCircle2 } from 'lucide-react'

type RedirectState = {
  open: boolean
  title: string
  seconds: number
  confirmLabel: string
  onComplete: (() => void) | null
  openDialog: (options: {
    title: string
    seconds?: number
    confirmLabel?: string
    onComplete: () => void
  }) => void
  closeDialog: () => void
}

const useRedirectDialogStore = create<RedirectState>(set => ({
  open: false,
  title: '',
  seconds: 5,
  confirmLabel: 'Continuar',
  onComplete: null,
  openDialog: ({ title, seconds = 5, confirmLabel = 'Continuar', onComplete }) =>
    set({ open: true, title, seconds, confirmLabel, onComplete }),
  closeDialog: () => set({ open: false, onComplete: null })
}))

export function showRedirectCountdownDialog(options: {
  title: string
  seconds?: number
  confirmLabel?: string
}): Promise<void> {
  return new Promise(resolve => {
    useRedirectDialogStore.getState().openDialog({
      ...options,
      onComplete: () => {
        useRedirectDialogStore.getState().closeDialog()
        resolve()
      }
    })
  })
}

export function RedirectCountdownHost() {
  const { open, title, seconds, confirmLabel, onComplete, closeDialog } =
    useRedirectDialogStore()
  const [remaining, setRemaining] = useState(seconds)

  useEffect(() => {
    if (!open) return
    setRemaining(seconds)
  }, [open, seconds])

  useEffect(() => {
    if (!open) return undefined

    const interval = setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) {
          clearInterval(interval)
          onComplete?.()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [open, onComplete])

  const handleContinue = () => {
    onComplete?.()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) closeDialog()
      }}
    >
      <DialogContent className="[&>button]:hidden">
        <DialogHeader className="items-center text-center">
          <CheckCircle2 className="mb-2 h-10 w-10 text-green-600" aria-hidden />
          <DialogTitle>{title}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Você será redirecionado em{' '}
            <span className="font-semibold tabular-nums">{remaining}</span>{' '}
            segundos...
          </p>
        </DialogHeader>
        <DialogFooter>
          <Button className="w-full sm:w-auto" onClick={handleContinue}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
